/* Lilia Padilla — free shipping line in the cart.
 *
 * Adds one line of copy directly above the CHECK OUT button, in both the slide-out
 * drawer and the cart page. Horizon has no block for this, and the cart summary is a
 * static snippet with nowhere to drop custom text, so it is inserted at runtime.
 *
 * Written in sentence case and uppercased in CSS. A screen reader announces the real
 * characters, so the line is read as a sentence rather than spelled out letter by letter,
 * which is what some readers do with caps.
 *
 * THRESHOLD, set 2026-09-05. $350 is chosen against the actual catalogue —
 * $285 / $385 / $650 / $1,195 / $1,195 — so that only the entry silver Chevalier pays
 * postage and the other four ship free. $300 and $400 both land within $15 of a product
 * price and read as petty; $500 additionally charges Perlarum, and with a median order
 * near $650 it would be mostly decorative anyway.
 *
 * Note that shipping cost in jewellery rises with insured value, so a high threshold is
 * inverted: it gives free delivery to the orders that cost most to ship. If the number
 * changes here it MUST also change in Settings -> Shipping and delivery, or the cart
 * promises a rate checkout will not honour.
 *
 * The drawer re-renders its whole summary whenever the cart changes — Shopify replaces
 * the markup via the Section Rendering API — so a one-time insert is wiped the moment
 * someone changes a quantity. A MutationObserver puts it back. The observer is
 * disconnected around the insert so it cannot see its own change and loop.
 *
 * Idempotent by design: place() bails if the line is already a sibling of that
 * particular .cart__ctas, so multiple triggers cannot stack duplicates. Each cart
 * context gets its own line, which is why the check is per-container.
 */
(function () {
  var TEXT = 'Free shipping on all orders over $350';
  var observer;

  function place() {
    document.querySelectorAll('.cart__ctas').forEach(function (ctas) {
      var prev = ctas.previousElementSibling;
      if (prev && prev.classList && prev.classList.contains('lp-shipping-note')) return;
      var p = document.createElement('p');
      p.className = 'lp-shipping-note';
      p.textContent = TEXT;
      ctas.parentNode.insertBefore(p, ctas);
    });
  }

  function run() {
    if (observer) observer.disconnect();
    place();
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    observer = new MutationObserver(function () {
      // Cheap guard: only act when a cart CTA exists without our line in front of it.
      var needed = false;
      document.querySelectorAll('.cart__ctas').forEach(function (c) {
        var p = c.previousElementSibling;
        if (!(p && p.classList && p.classList.contains('lp-shipping-note'))) needed = true;
      });
      if (needed) run();
    });
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* Open the cart drawer after ADD TO CART, at half the screen — Lilia's request 2026-09-14.
 * (Replaces the same-day version that went to the full cart page.)
 *
 * Horizon adds by fetch and stays on the product page, and its drawer only opens on add when
 * the `auto-open` setting is on, which it is not here. When the add succeeds, the product form
 * dispatches a bubbling `cart:update` event with data.source 'product-form-component' (the
 * same form powers quick-add). Failed adds set didError and are left alone, so the theme's
 * error message shows. The width is set in lp-cart.css. If a page has no drawer, it falls
 * back to the cart page.
 */
(function () {
  document.addEventListener('cart:update', function (e) {
    var d = e.detail && e.detail.data;
    if (!d || d.source !== 'product-form-component' || d.didError) return;
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.showDialog === 'function') {
      if (!(drawer.refs && drawer.refs.dialog && drawer.refs.dialog.open)) drawer.showDialog();
      return;
    }
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    window.location.assign(root + 'cart');
  });
})();

/* No full cart page — Lilia's request 2026-09-14: the cart only ever opens half way.
 *
 * A visit to /cart (a typed or shared link, or "Return to cart" from checkout) goes back to
 * the page the shopper came from, or home, and opens the half-screen drawer there. The
 * sessionStorage flag carries the request across the page load. If storage is blocked, the
 * redirect is skipped and the cart page still works rather than looping.
 */
(function () {
  var KEY = 'lp-open-cart-drawer';
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var cartPath = root + 'cart';

  if (window.location.pathname.replace(/\/$/, '') === cartPath.replace(/\/$/, '')) {
    try { sessionStorage.setItem(KEY, '1'); } catch (e) { return; }
    var back = root;
    try {
      var ref = new URL(document.referrer);
      if (ref.origin === window.location.origin && ref.pathname.replace(/\/$/, '') !== cartPath.replace(/\/$/, '')) {
        back = ref.pathname + ref.search;
      }
    } catch (e) {}
    window.location.replace(back);
    return;
  }

  var want;
  try { want = sessionStorage.getItem(KEY); sessionStorage.removeItem(KEY); } catch (e) {}
  if (!want) return;
  function open(tries) {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.showDialog === 'function') { drawer.showDialog(); return; }
    if (tries > 0) setTimeout(function () { open(tries - 1); }, 150);
  }
  if (document.readyState === 'complete') open(20);
  else window.addEventListener('load', function () { open(20); });
})();

/* Drawer heading reads "Your Bag" — Lilia's request 2026-09-14 (briefly "My Bag").
 *
 * The heading text is a theme translation string ("Cart"), and the primary-locale strings
 * cannot be overridden through the API here, so the bare text node before the item-count
 * bubble is replaced in place. The bubble element is untouched. An observer re-applies it if
 * the drawer is re-rendered. The dialog is aria-labelledby this heading, so screen readers
 * announce "Your Bag" too.
 */
(function () {
  var LABEL = 'Your Bag';
  function apply() {
    var h = document.getElementById('cart-drawer-heading');
    if (!h) return;
    for (var i = 0; i < h.childNodes.length; i++) {
      var n = h.childNodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) {
        if (n.textContent.trim() !== LABEL) n.textContent = ' ' + LABEL + ' ';
        return;
      }
    }
  }
  /* Header cart link reads "Bag" (shown uppercase by the header's text style) — Lilia's request
   * 2026-09-14, after a brief switch to the bag icon. The desktop text span's "Cart" is replaced
   * the same way, and the button's aria-label follows so screen readers match. */
  function applyHeader() {
    document.querySelectorAll('.header-actions__cart-icon').forEach(function (icon) {
      var span = icon.querySelector(':scope > .mobile\\:hidden');
      if (span && span.textContent.trim() !== 'Bag') span.textContent = ' Bag ';
      var btn = icon.closest('button, a');
      if (btn && btn.getAttribute('aria-label') === 'Cart') btn.setAttribute('aria-label', 'Bag');
    });
  }
  function run() { apply(); applyHeader(); }
  function start() {
    run();
    new MutationObserver(run).observe(document.body, { childList: true, subtree: true, characterData: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* ---- Saved pieces (the bookmark) — Lilia, 2026-09-20: "add the bookmark 1 to the website" ----
 * The store has no wishlist, so this is a small one that lives entirely in the theme:
 *   · a bookmark beside the product title — outline when not saved, filled when saved;
 *   · a SAVED link in the desktop header, shown only once something is saved, opening a card in the
 *     same family as Currency, Search and Account that lists the saved pieces with Remove.
 * Saved pieces are kept in this browser (localStorage) — no account, no app, nothing sent anywhere.
 * That also means they do not follow a visitor to another device. Bookmark i is the drawing from
 * the icon sheet (00-brand/icons/final/bookmark.svg).
 */
(function () {
  var KEY = 'lp-saved';
  // The mark is Bookmark i again, the ribbon with a notched tail from the icon sheet (Lilia, 2026-10-05: "I dont like
  // the heart bookmark, use Bookmark i instead"). It was a heart for a day (2026-10-04). A line when the piece is not
  // saved, the same shape filled once it is, as on Doen's cards ("I want something like this"); lp-cart.css does the
  // filling and the colour. The saved pieces are read by the account pop-up's SAVED tab (lp-dividers.js).
  var MARK = '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">' +
             '<path d="M7 3.5h10v17l-5-4.2-5 4.2z" ' +
             'stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>';

  function read() { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } }
  function write(list) { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {} paint(); }
  function has(handle) { return read().some(function (p) { return p.handle === handle; }); }

  function current() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return null;
    var og = document.querySelector('meta[property="og:title"]');
    return { handle: m[1], title: (og && og.content) || document.title.split(' – ')[0], url: location.pathname };
  }

  /* product page: the bookmark */
  function productButton() {
    var p = current();
    if (!p || !onShopPage() || document.querySelector('.lp-save')) return;
    var want = p.title.trim().toLowerCase(), heads = document.querySelectorAll('main h1, main h2, main h3'), i, title;
    for (i = 0; i < heads.length; i++) {
      if (heads[i].offsetParent && heads[i].textContent.trim().toLowerCase() === want && !heads[i].closest('product-card, .resource-list')) { title = heads[i]; break; }
    }
    if (!title) return;
    // Lilia, 2026-09-20: "remove the bookmark on the corner, i want it to sit ontop of the image right side
    // corner" — it now sits on the product photograph's top-right corner; beside the title only if a
    // product somehow has no gallery.
    var gallery = document.querySelector('main media-gallery'), host = gallery || title.parentElement,
        b = document.createElement('button');
    host.classList.add('lp-save-host'); if (gallery) host.classList.add('lp-save-host--media');
    b.type = 'button'; b.className = 'lp-save'; b.innerHTML = MARK;
    b.addEventListener('click', function () {
      var list = read();
      if (has(p.handle)) list = list.filter(function (x) { return x.handle !== p.handle; }); else list.push(p);
      write(list);
    });
    host.appendChild(b);
  }

  /* header: SAVED link and its card */
  function headerLink() {
    var actions = document.querySelector('header-actions');
    if (!actions || actions.querySelector('.lp-saved')) return;
    var cart = actions.querySelector('cart-drawer-component'), wrap = document.createElement('div');
    wrap.className = 'lp-saved header-actions__action';
    wrap.innerHTML = '<button type="button" class="lp-saved__link header-actions__text-style" aria-expanded="false">Saved</button>' +
                     '<div class="lp-saved__card" hidden><p class="lp-saved__title">Saved</p><ul class="lp-saved__list"></ul></div>';
    actions.insertBefore(wrap, cart || null);
    var link = wrap.querySelector('.lp-saved__link'), card = wrap.querySelector('.lp-saved__card');
    function close() { card.hidden = true; link.setAttribute('aria-expanded', 'false'); }
    link.addEventListener('click', function (e) {
      e.stopPropagation();
      var open = card.hidden;
      if (open) card.style.right = Math.max(16, document.documentElement.clientWidth - link.getBoundingClientRect().right) + 'px'; // clientWidth, not innerWidth: the page scrollbar is not part of the layout
      card.hidden = !open; link.setAttribute('aria-expanded', String(open));
    });
    card.addEventListener('click', function (e) {
      e.stopPropagation();
      var rm = e.target.closest('[data-remove]');
      if (rm) write(read().filter(function (x) { return x.handle !== rm.getAttribute('data-remove'); }));
    });
    document.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* product cards (collection pages, home page rows, "From the studio"): a bookmark on each photograph.
     Lilia, 2026-09-20: "I want the bookmark icon to show up on the products right top corner." The card's
     own link covers the whole photograph, so the button sits above it and swallows its own click. */
  // the shop pages, and only those: see the matching rule in lp-cart.css
  function onShopPage() {
    var m = document.querySelector('main[data-template]');
    if (!m) return true;                       // unlabelled: leave the bookmarks alone
    var t = m.getAttribute('data-template') || '';
    return /^(collection|product)/.test(t);    // also collection.chevalier, product.ark-angel-sapphire-studs and the like
  }

  function cardButtons() {
    if (!onShopPage()) return;
    var cards = document.querySelectorAll('product-card'), i;
    for (i = 0; i < cards.length; i++) (function (card) {
      if (card.querySelector('.lp-save')) return;
      var link = card.querySelector('a[href*="/products/"]'), media = card.querySelector('.card-gallery');
      if (!link || !media) return;
      var m = link.getAttribute('href').match(/\/products\/([^\/?#]+)/);
      if (!m) return;
      var t = card.querySelector('.visually-hidden, [class*="title"], h3, h2'),
          item = { handle: m[1], title: (t && t.textContent.trim()) || m[1].replace(/-/g, ' '), url: '/products/' + m[1] },
          b = document.createElement('button');
      b.type = 'button'; b.className = 'lp-save lp-save--card'; b.innerHTML = MARK; b.setAttribute('data-handle', item.handle);
      b.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var list = read();
        if (has(item.handle)) list = list.filter(function (x) { return x.handle !== item.handle; }); else list.push(item);
        write(list);
      });
      media.classList.add('lp-save-host', 'lp-save-host--media');
      media.appendChild(b);
    })(cards[i]);
  }

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function paint() {
    var list = read(), p = current(), b = document.querySelector('.lp-save'), wrap = document.querySelector('.lp-saved');
    if (b && p) {
      var on = has(p.handle);
      b.classList.toggle('is-saved', on);
      b.setAttribute('aria-pressed', String(on));
      b.setAttribute('aria-label', on ? 'Remove from saved' : 'Save this piece');
    }
    var cb = document.querySelectorAll('.lp-save--card'), k, on2;
    for (k = 0; k < cb.length; k++) {
      on2 = has(cb[k].getAttribute('data-handle'));
      cb[k].classList.toggle('is-saved', on2); cb[k].setAttribute('aria-pressed', String(on2));
      cb[k].setAttribute('aria-label', on2 ? 'Remove from saved' : 'Save this piece');
    }
    if (wrap) {
      wrap.classList.toggle('has-items', list.length > 0);
      if (!list.length) wrap.querySelector('.lp-saved__card').hidden = true;
      wrap.querySelector('.lp-saved__list').innerHTML = list.map(function (x) {
        return '<li><a href="' + esc(x.url) + '">' + esc(x.title) + '</a><button type="button" data-remove="' + esc(x.handle) + '">Remove</button></li>';
      }).join('');
    }
  }

  function run() { productButton(); headerLink(); cardButtons(); paint(); }
  // cards are added later by search, recommendations and pagination: look again when the page changes
  var again; try { new MutationObserver(function () { clearTimeout(again); again = setTimeout(function () { cardButtons(); paint(); }, 250); })
    .observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
    window.addEventListener('storage', function (e) { if (e.key === KEY) paint(); });
  } catch (e) { /* no bookmark; nothing else is affected */ }
})();

/* ---- "items left in your bag" ----
 * Lilia, 2026-09-23. A quiet reminder for a visitor who already has something in the bag: a small card in the
 * same family as Currency, Search and Account, a few seconds after the page settles. Once per visit, never on
 * the cart page itself, dismissible, and it takes itself away after nine seconds. The count comes from
 * /cart.js, so it is always the real bag.
 */
(function () {
  var KEY = 'lp-bag-reminder';

  function show(n) {
    if (document.querySelector('.lp-nudge')) return;
    var d = document.createElement('div');
    d.className = 'lp-nudge';
    d.setAttribute('role', 'status');
    d.innerHTML = '<p class="lp-nudge__text">' + n + (n === 1 ? ' item' : ' items') + ' left in your bag</p>' +
                  '<button type="button" class="lp-nudge__open">View bag</button>' +
                  '<button type="button" class="lp-nudge__close" aria-label="Dismiss">×</button>';
    document.body.appendChild(d);
    requestAnimationFrame(function () { d.classList.add('is-in'); });

    var timer = setTimeout(hide, 9000);
    function hide() {
      clearTimeout(timer);
      d.classList.remove('is-in');
      setTimeout(function () { if (d.parentNode) d.parentNode.removeChild(d); }, 400);
    }
    d.querySelector('.lp-nudge__close').addEventListener('click', hide);
    d.querySelector('.lp-nudge__open').addEventListener('click', function () {
      hide();
      var cd = document.querySelector('cart-drawer-component');
      if (cd && typeof cd.showDialog === 'function') cd.showDialog(); else location.href = '/cart';
    });
  }

  function run() {
    try { if (sessionStorage.getItem(KEY)) return; } catch (e) {}
    if (/^\/cart/.test(location.pathname)) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (!c || !c.item_count) return;
        if (document.querySelector('.cart-drawer__dialog[open]')) return;   // they are already looking at it
        try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
        show(c.item_count);
      })
      .catch(function () {});
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(run, 2500); });
    else setTimeout(run, 2500);
  } catch (e) { /* no reminder */ }
})();

/* ---- the bag fades back while it changes ----
 * Lilia, 2026-10-05: "When i add a new item to the cart or a new quantity i want it so the pop up darkens sort of like
 * these next to images", of Doen's bag, whose contents fade back while the bag is being changed and return once it
 * has its answer. So <html> carries lp-bag-busy from the moment a quantity or Remove is pressed in the bag, or a piece
 * is added to it, until the theme has its answer, and lp-cart.css fades the bag's contents for that long. Never for
 * less than MIN, or a quick answer would only flicker. The class sits on <html> because the theme redraws the bag
 * from the server's copy and would wipe a class put on the bag itself.
 */
(function () {
  var root = document.documentElement, MIN = 120, since = 0, off, watch;   // 450 → 280 → 120, with the fade's 0.25s → 0.16s → 0.1s in lp-cart.css: "move a little faster", then "I want the animation faster" (2026-10-09)
  function on() {
    clearTimeout(off);
    if (!root.classList.contains('lp-bag-busy')) { since = Date.now(); root.classList.add('lp-bag-busy'); }
  }
  function done() {
    clearTimeout(off); clearInterval(watch);
    off = setTimeout(function () { root.classList.remove('lp-bag-busy'); }, Math.max(0, MIN - (Date.now() - since)));
  }
  // The theme marks the bag (cart-items-disabled) while its request is out, and clears the mark when it is back,
  // whether it went well or not. Wait for the mark to come and go; give up after six seconds.
  function wait() {
    var seen = false, n = 0;
    clearInterval(watch);
    watch = setInterval(function () {
      var busy = !!document.querySelector('cart-items-component.cart-items-disabled');
      if (busy) seen = true;
      if ((seen && !busy) || ++n > 40) done();
    }, 150);
  }
  function inBag(t) { return t instanceof Element && !!t.closest('cart-items-component'); }
  document.addEventListener('quantity-selector:update', function (e) { if (inBag(e.target)) { on(); wait(); } });
  document.addEventListener('click', function (e) {
    if (e.target instanceof Element && e.target.closest('cart-items-component .cart-items__remove')) { on(); wait(); }
  });
  document.addEventListener('cart:update', function () { on(); done(); });      // a piece added, or any change answered
})();
